/**
 * PSP/CRE Tool v2.0 - Night Operations Configuration Constants
 * 
 * Purpose: Define night operations impact on medical support,
 * including MEDEVAC restrictions, treatment delays, and DNBI effects.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 2024
 * Version: 1.0
 * 
 * References:
 * - NATO AJMedP-1 Allied Joint Medical Planning Doctrine
 * - FM 4-02.2 Medical Evacuation
 * - USAF Aeromedical Evacuation night operations doctrine
 */

// ============================================================================
// NIGHT OPERATIONS LEVELS
// Overall percentage of operations conducted at night
// ============================================================================

export const NIGHT_OPS_LEVELS = {
  NONE: {
    value: 'NONE',
    label: 'No Night Operations',
    shortLabel: 'NONE',
    description: 'All operations during daylight hours',
    icon: '☀️',
    color: 'success',
    
    night_ops_percentage: 0,
    
    // Impact modifiers
    casualty_modifier: 1.0,
    friendly_fire_risk: 0.02,
    navigation_error_risk: 0.05,
    
    // Medical impacts
    medevac_availability: 1.0,
    ground_evac_speed: 1.0,
    treatment_quality: 1.0,
    
    notes: 'Standard daylight operations. No night-specific planning required.'
  },
  
  LIMITED: {
    value: 'LIMITED',
    label: 'Limited Night Ops (25%)',
    shortLabel: '25%',
    description: 'Some critical operations at night',
    icon: '🌅',
    color: 'info',
    
    night_ops_percentage: 25,
    
    casualty_modifier: 1.05,
    friendly_fire_risk: 0.04,
    navigation_error_risk: 0.08,
    
    medevac_availability: 0.95,
    ground_evac_speed: 0.9,
    treatment_quality: 0.95,
    
    notes: 'Limited night operations. NVG-equipped MEDEVAC recommended.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Night Ops (50%)',
    shortLabel: '50%',
    description: 'Significant night operations tempo',
    icon: '🌙',
    color: 'warning',
    
    night_ops_percentage: 50,
    
    casualty_modifier: 1.1,
    friendly_fire_risk: 0.06,
    navigation_error_risk: 0.12,
    
    medevac_availability: 0.85,
    ground_evac_speed: 0.8,
    treatment_quality: 0.9,
    
    nvg_required: true,
    
    notes: 'NVG equipment essential. Increased coordination time for MEDEVAC.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Night Ops (75%)',
    shortLabel: '75%',
    description: 'Majority of operations at night',
    icon: '🌑',
    color: 'warning',
    
    night_ops_percentage: 75,
    
    casualty_modifier: 1.15,
    friendly_fire_risk: 0.08,
    navigation_error_risk: 0.15,
    
    medevac_availability: 0.7,
    ground_evac_speed: 0.7,
    treatment_quality: 0.85,
    
    nvg_required: true,
    forward_surgical_critical: true,
    
    notes: 'Full NVG capability required. Forward surgical teams essential.'
  },
  
  CONTINUOUS: {
    value: 'CONTINUOUS',
    label: 'Continuous Ops (24hr)',
    shortLabel: '24HR',
    description: '24-hour operations cycle',
    icon: '🔄',
    color: 'error',
    
    night_ops_percentage: 50,     // 50% night by definition
    
    casualty_modifier: 1.2,
    friendly_fire_risk: 0.07,
    navigation_error_risk: 0.12,
    fatigue_factor: 1.3,
    
    medevac_availability: 0.75,
    ground_evac_speed: 0.75,
    treatment_quality: 0.85,
    
    nvg_required: true,
    crew_rest_critical: true,
    
    notes: 'Crew rest management critical. Plan for personnel rotation.'
  }
};

// ============================================================================
// ILLUMINATION CONDITIONS
// Ambient light conditions affecting operations
// ============================================================================

export const ILLUMINATION_CONDITIONS = {
  FULL_MOON: {
    value: 'FULL_MOON',
    label: 'Full Moon',
    shortLabel: 'FULL',
    description: '50-100% illumination',
    icon: '🌕',
    color: 'success',
    
    illumination_percent: 100,
    
    visibility_modifier: 0.7,     // 70% of daylight
    nvg_effectiveness: 1.0,
    unaided_movement: true,
    
    medevac_modifier: 0.95,
    ground_evac_modifier: 0.9,
    
    notes: 'Good natural illumination. Unaided movement possible with care.'
  },
  
  QUARTER_MOON: {
    value: 'QUARTER_MOON',
    label: 'Quarter Moon',
    shortLabel: 'QTR',
    description: '25-50% illumination',
    icon: '🌓',
    color: 'info',
    
    illumination_percent: 35,
    
    visibility_modifier: 0.4,
    nvg_effectiveness: 1.0,
    unaided_movement: false,
    
    medevac_modifier: 0.85,
    ground_evac_modifier: 0.8,
    
    notes: 'NVG recommended for all operations.'
  },
  
  NEW_MOON: {
    value: 'NEW_MOON',
    label: 'New Moon/Overcast',
    shortLabel: 'NEW',
    description: '0-25% illumination',
    icon: '🌑',
    color: 'warning',
    
    illumination_percent: 10,
    
    visibility_modifier: 0.2,
    nvg_effectiveness: 0.7,       // Limited without ambient light
    unaided_movement: false,
    
    medevac_modifier: 0.7,
    ground_evac_modifier: 0.6,
    
    ir_illumination_recommended: true,
    
    notes: 'Very dark. IR illumination may be required for NVG.'
  },
  
  ARTIFICIAL: {
    value: 'ARTIFICIAL',
    label: 'Artificial Illumination',
    shortLabel: 'ARTIF',
    description: 'Battlefield illumination in use',
    icon: '💡',
    color: 'info',
    
    illumination_percent: 80,
    
    visibility_modifier: 0.8,
    nvg_effectiveness: 0.5,       // Can washout NVGs
    unaided_movement: true,
    
    medevac_modifier: 0.9,
    ground_evac_modifier: 0.85,
    
    enemy_observation_risk: 1.3,
    
    notes: 'Good visibility but increases enemy observation. NVG washout possible.'
  }
};

// ============================================================================
// NIGHT VISION EQUIPMENT AVAILABILITY
// NVG/thermal equipment for medical units
// ============================================================================

export const NVG_AVAILABILITY = {
  FULL: {
    value: 'FULL',
    label: 'Full NVG/Thermal',
    shortLabel: 'FULL',
    description: 'All medical personnel and vehicles NVG equipped',
    icon: '👓',
    color: 'success',
    
    coverage_percent: 100,
    
    // Capability levels
    personnel_nvg: true,
    vehicle_nvg: true,
    helicopter_nvg: true,
    thermal_imaging: true,
    
    night_capability_modifier: 0.95,
    
    notes: 'Full night operations capability.'
  },
  
  PARTIAL: {
    value: 'PARTIAL',
    label: 'Partial NVG',
    shortLabel: 'PARTIAL',
    description: 'Key personnel and aviation only',
    icon: '⚠️',
    color: 'warning',
    
    coverage_percent: 50,
    
    personnel_nvg: false,
    vehicle_nvg: true,           // Drivers
    helicopter_nvg: true,
    thermal_imaging: false,
    
    night_capability_modifier: 0.75,
    
    notes: 'Ground medical personnel lack NVG. Aviation capable.'
  },
  
  AVIATION_ONLY: {
    value: 'AVIATION_ONLY',
    label: 'Aviation Only',
    shortLabel: 'AIR',
    description: 'Only MEDEVAC aircraft have NVG',
    icon: '🚁',
    color: 'warning',
    
    coverage_percent: 25,
    
    personnel_nvg: false,
    vehicle_nvg: false,
    helicopter_nvg: true,
    thermal_imaging: false,
    
    night_capability_modifier: 0.5,
    
    notes: 'Air MEDEVAC capable at night. Ground operations limited.'
  },
  
  NONE: {
    value: 'NONE',
    label: 'No Night Vision',
    shortLabel: 'NONE',
    description: 'No NVG equipment available',
    icon: '❌',
    color: 'error',
    
    coverage_percent: 0,
    
    personnel_nvg: false,
    vehicle_nvg: false,
    helicopter_nvg: false,
    thermal_imaging: false,
    
    night_capability_modifier: 0.2,
    
    notes: 'Night operations severely restricted. Use illumination or delay.'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get combined night operations modifiers
 */
export const getNightOpsModifiers = (nightConfig) => {
  const {
    night_ops_level = 'NONE',
    illumination = 'QUARTER_MOON',
    nvg_availability = 'FULL',
    current_phase_night = false
  } = nightConfig;
  
  const opsLevel = NIGHT_OPS_LEVELS[night_ops_level] || NIGHT_OPS_LEVELS.NONE;
  const illum = ILLUMINATION_CONDITIONS[illumination] || ILLUMINATION_CONDITIONS.QUARTER_MOON;
  const nvg = NVG_AVAILABILITY[nvg_availability] || NVG_AVAILABILITY.FULL;
  
  // Base modifiers from ops level
  let casualtyMod = opsLevel.casualty_modifier || 1.0;
  let medevacMod = opsLevel.medevac_availability || 1.0;
  let groundEvacMod = opsLevel.ground_evac_speed || 1.0;
  let treatmentMod = opsLevel.treatment_quality || 1.0;
  
  // Adjust for illumination (only if actually operating at night)
  const nightPortion = opsLevel.night_ops_percentage / 100;
  if (nightPortion > 0) {
    medevacMod *= (1 - nightPortion) + (nightPortion * illum.medevac_modifier);
    groundEvacMod *= (1 - nightPortion) + (nightPortion * illum.ground_evac_modifier);
  }
  
  // Adjust for NVG availability
  if (nightPortion > 0 && nvg.night_capability_modifier < 1.0) {
    const nvgImpact = 1 - ((1 - nvg.night_capability_modifier) * nightPortion);
    medevacMod *= nvgImpact;
    groundEvacMod *= nvgImpact;
  }
  
  // Generate warnings
  const warnings = [];
  
  if (opsLevel.nvg_required && nvg.value !== 'FULL') {
    warnings.push({
      severity: 'warning',
      category: 'NIGHT_OPS',
      message: `Night ops level (${opsLevel.shortLabel}) requires full NVG but only ${nvg.label} available`,
      recommendation: 'Reduce night operations or acquire NVG equipment'
    });
  }
  
  if (opsLevel.value === 'CONTINUOUS' && !opsLevel.crew_rest_critical) {
    warnings.push({
      severity: 'warning',
      category: 'FATIGUE',
      message: 'Continuous operations increase fatigue-related errors',
      recommendation: 'Implement crew rest rotation schedule'
    });
  }
  
  if (nvg.value === 'NONE' && nightPortion > 0.25) {
    warnings.push({
      severity: 'error',
      category: 'NIGHT_OPS',
      message: 'Significant night operations without NVG capability',
      recommendation: 'Restrict operations to daylight or acquire NVG'
    });
  }
  
  if (illum.ir_illumination_recommended && nvg.value !== 'NONE') {
    warnings.push({
      severity: 'info',
      category: 'ILLUMINATION',
      message: 'Low ambient light - IR illumination recommended for NVG',
      recommendation: 'Ensure IR flood/spot lights available'
    });
  }
  
  // Warning if currently in night phase with limited capability
  if (current_phase_night && nvg.night_capability_modifier < 0.75) {
    warnings.push({
      severity: 'warning',
      category: 'CURRENT_OPS',
      message: 'Currently in night phase with limited NVG capability',
      recommendation: 'Consider delaying non-urgent evacuations until daylight'
    });
  }
  
  return {
    // Primary modifiers
    casualty_modifier: Math.round(casualtyMod * 100) / 100,
    medevac_modifier: Math.round(medevacMod * 100) / 100,
    ground_evac_modifier: Math.round(groundEvacMod * 100) / 100,
    treatment_modifier: Math.round(treatmentMod * 100) / 100,
    
    // Risk factors
    friendly_fire_risk: opsLevel.friendly_fire_risk || 0.02,
    navigation_error_risk: opsLevel.navigation_error_risk || 0.05,
    fatigue_factor: opsLevel.fatigue_factor || 1.0,
    
    // Night portion
    night_ops_percentage: opsLevel.night_ops_percentage,
    current_phase_night,
    
    // Equipment requirements
    nvg_required: opsLevel.nvg_required || false,
    nvg_available: nvg.coverage_percent,
    forward_surgical_critical: opsLevel.forward_surgical_critical || false,
    
    // Warnings
    warnings
  };
};

/**
 * Validate night operations configuration
 */
export const validateNightOpsConfig = (nightConfig) => {
  const errors = [];
  const warnings = [];
  
  const opsLevel = NIGHT_OPS_LEVELS[nightConfig.night_ops_level];
  const nvg = NVG_AVAILABILITY[nightConfig.nvg_availability];
  
  if (opsLevel?.night_ops_percentage > 50 && nvg?.value === 'NONE') {
    errors.push('Cannot conduct >50% night operations without any NVG capability');
  }
  
  if (opsLevel?.value === 'CONTINUOUS' && !nightConfig.crew_rest_plan) {
    warnings.push('Continuous operations without crew rest plan increases error risk');
  }
  
  return { errors, warnings, isValid: errors.length === 0 };
};

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_NIGHT_OPS_CONFIG = {
  enabled: false,
  night_ops_level: 'NONE',
  illumination: 'QUARTER_MOON',
  nvg_availability: 'FULL'
};

export const CONVENTIONAL_NIGHT_OPS_CONFIG = {
  enabled: true,
  night_ops_level: 'MODERATE',
  illumination: 'QUARTER_MOON',
  nvg_availability: 'FULL'
};

export const HIGH_INTENSITY_NIGHT_OPS_CONFIG = {
  enabled: true,
  night_ops_level: 'CONTINUOUS',
  illumination: 'NEW_MOON',
  nvg_availability: 'PARTIAL'
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// Named default export (fixes lint warning)
const nightOperationsConstants = {
  NIGHT_OPS_LEVELS,
  ILLUMINATION_CONDITIONS,
  NVG_AVAILABILITY,
  getNightOpsModifiers,
  validateNightOpsConfig,
  DEFAULT_NIGHT_OPS_CONFIG,
  CONVENTIONAL_NIGHT_OPS_CONFIG,
  HIGH_INTENSITY_NIGHT_OPS_CONFIG
};

export default nightOperationsConstants;
